import { useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Logo from '@comp/Logo/Logo';
import P2 from '@base/P2/P2';
import H7 from '@base/H7/H7';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Facebook from '@icon/Facebook';
import Youtube from '@icon/Youtube';
import Twitter from '@icon/Twitter';
import Instagram from '@icon/Instagram';
import LinkedIn from '@icon/LinkedIn';
import Playstore from '@images/playStore_resized.svg';
import Appstore from '@images/appStore_resized.svg';
import { breakpoints, Fonts, Spacings } from '@styles/index';

const MarginBottom = styled.div`
  padding-top: ${Spacings.SPACING_9B};
`;

const StyledAnchor = styled.a`
  color: ${({ theme }) => theme.colors.fontGrey};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  font-size: ${Fonts.P2};
  font-weight: ${Fonts.REGULAR};
  transition: color 200ms ease-in-out;
  ${({ anchorStyle }) => anchorStyle}
  &:hover {
    color: ${({ theme, isEducator }) =>
      isEducator ? theme.colors.blue : theme.colors.green};
  }
`;

const FooterContainer = styled.footer`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme, floating }) =>
    floating && boxShadowGenerator({ theme })};
  width: 100%;
  margin: auto;
  margin-top: ${({ floating }) =>
    !floating && `${Spacings.SPACING_HEADER_MARGIN}px`};
  padding: ${({ floating }) =>
    floating
      ? `${Spacings.SPACING_9B} 0 ${Spacings.SPACING_9B} ${Spacings.SPACING_6B}`
      : `${Spacings.SPACING_10B} 0px`};
  border-radius: ${({ floating }) => (floating ? '8px' : '0px')};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    display: block;
    padding: 40px 16px 40px 16px;
    box-shadow: 0px -16px 32px rgba(221, 230, 237, 0.4);
  }
`;

const FooterWrapper = styled.div`
  width: ${({ floating }) => (floating ? '690px' : '1136px')};
  margin: auto;
  display: grid;
  grid-template-columns: ${({ floating }) =>
    floating ? 'repeat(22, 1fr)' : 'repeat(24, 1fr)'};
  grid-column-gap: ${({ floating }) => (floating ? '18px' : '16px')};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 728px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`;

const UNInfo = styled.div`
  grid-column: ${({ floating }) => (floating ? '2/10' : 'span 6')};
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: 40px;
    width: 100%;
  }
`;

const SocialImage = styled.div`
  margin-right: ${Spacings.SPACING_3B};
  user-drag: none;
  user-select: none;
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  transition: background-color 200ms ease;
  background-color: ${({ theme }) => theme.colors.dropShadowV4};
  &:hover {
    background-color: ${({ theme }) => theme.colors.fontLightBlueV2};
  }
`;

const SocialImagesWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${({ floating }) =>
    floating ? Spacings.SPACING_9B : Spacings.SPACING_10B};
  display: flex;
`;

const DownloadAppsLearnerWrapper = styled.div`
  grid-column: 18/21;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: calc(50% - 8px);
  }
`;

const DownloadAppsEducatorWrapper = styled.div`
  grid-column: 22/25;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-left: 32px;
    display: none;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: block;
    width: calc(50% - 8px);
    margin-left: 0px;
  }
`;

const AppImage = styled(LazyLoadImage)`
  user-drag: none;
  user-select: none;
  display: block;
  margin: ${({ margin }) => `0 0 ${margin} 0`};
  transition: transform 200ms ease-in-out;
  &:hover {
    transform: translateY(-2px);
  }
`;

const StyledH7 = styled(H7)`
  margin-bottom: 16px;
`;

const LeftStyledUL = styled.ul`
  padding: 0px;
  grid-column: ${({ floating }) => (floating ? '11/16' : '8/11')};
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-bottom: 40px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: calc(50% - 8px);
  }
`;

const RightStyledUL = styled(LeftStyledUL)`
  grid-column: ${({ floating }) => (floating ? '17/22' : '12/15')};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-left: 40px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: calc(50% - 8px);
    margin-left: 0px;
  }
`;

const StyledList = styled.li`
  list-style-type: none;
  margin-bottom: ${Spacings.SPACING_2B};
  :last-child {
    margin-bottom: 0px;
  }
`;

const ExtraBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  span:last-child {
    display: none;
  }
`;

const Pipe = styled.span`
  color: ${({ theme }) => theme.colors.fontGrey};
  font-size: 12px;
  line-height: 150%;
`;

const OtherBlock = styled.div`
  grid-column: 8/19;
  margin-top: 40px;
`;

const socialIconsLink = [
  {
    name: 'facebook',
    link: 'https://www.facebook.com/unacademy/',
    icon: Facebook
  },
  {
    name: 'youtube',
    link: 'https://www.youtube.com/channel/UCABe2FgVNv2hgBeMu2mySVg',
    icon: Youtube
  },
  {
    name: 'twitter',
    link: 'https://twitter.com/unacademy',
    icon: Twitter
  },
  {
    name: 'instagram',
    link: 'https://www.instagram.com/unacademy/',
    icon: Instagram
  },
  {
    name: 'linkedin',
    link: 'https://www.linkedin.com/company/unacademy',
    icon: LinkedIn
  }
];

const linksListLeft = [
  { label: 'About us', href: '/about' },
  { label: 'Careers', href: 'http://careers.unacademy.com' },
  { label: 'Blogs', href: 'https://blog.unacademy.com' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms and Conditions', href: '/terms' }
];

const linksListRight = [
  { label: 'IIT JEE', href: '/goal/jee-main-and-advanced-preparation/TMUVD' },
  {
    label: 'UPSC CSE GS',
    href: '/goal/upsc-civil-services-examination-ias-preparation/KSCGY'
  },
  { label: 'SSC Exams', href: '/goal/ssc-exams/VLEMN' },
  { label: 'CBSE Class 12', href: '/goal/cbse-class-12/PLWCX' },
  { label: 'NEET UG', href: '/goal/neet-ug/YOTUH' }
];

const extraLinks = [
  { label: 'User Guidelines', href: '/guidelines' },
  { label: 'Site Map', href: '/sitemap' },
  { label: 'Refund Policy', href: '/refund-policy' },
  { label: 'Legal Notices', href: '/legal-notices' },
  {
    label: 'Plus subscriptions T&C',
    href: '/subscription-terms'
  }
];

const learnerAppInfo = [
  {
    label: 'Play Store',
    src: Playstore,
    link:
      'https://play.google.com/store/apps/details?id=com.unacademyapp&referrer=utm_source%3DFooter%26utm_medium%3DWebApp%26utm_campaign%3DFooter%26',
    height: Spacings.SPACING_9B,
    width: Spacings.SPACING_32B,
    margin: Spacings.SPACING_2B
  },
  {
    label: 'App Store',
    src: Appstore,
    link:
      'https://apps.apple.com/in/app/unacademy-learning-app/id1342565069?referrer%3Dutm_source%253DFooter%2526utm_medium%253DWebApp%2526utm_campaign%253DFooter%2526',
    height: Spacings.SPACING_10B,
    width: Spacings.SPACING_32B,
    margin: 0
  }
];

const educatorAppInfo = [
  {
    label: 'Play Store',
    src: Playstore,
    link:
      'https://play.google.com/store/apps/details?id=com.unacademy&referrer=utm_source%3DFooter%26utm_medium%3DWebApp%26utm_campaign%3DFooter%26',
    height: Spacings.SPACING_9B,
    width: Spacings.SPACING_32B,
    margin: Spacings.SPACING_2B
  },
  {
    label: 'App Store',
    src: Appstore,
    link:
      'https://apps.apple.com/in/app/unacademy-educator-app/id1156375099?referrer%3Dutm_source%253DFooter%2526utm_medium%253DWebApp%2526utm_campaign%253DFooter%2526',
    height: Spacings.SPACING_10B,
    width: Spacings.SPACING_32B,
    margin: 0
  }
];

const learnerApp = learnerAppInfo.map((app) => (
  <a href={app.link} key={app.label} target="_blank" rel="noopener noreferrer">
    <AppImage
      src={app.src}
      alt={app.label}
      width={app.width}
      margin={app.margin}
    />
  </a>
));

const educatorApp = educatorAppInfo.map((app) => (
  <a href={app.link} key={app.label} target="_blank" rel="noopener noreferrer">
    <AppImage
      src={app.src}
      alt={app.label}
      width={app.width}
      margin={app.margin}
    />
  </a>
));

const BaseFooter = ({
  display,
  noFooter,
  floating,
  hideLogo,
  isEducator,
  className
}) => {
  const theme = useContext(ThemeContext);
  if (!display) return !noFooter ? <MarginBottom /> : null;

  const socialIcons = socialIconsLink.map((social) => {
    const Icon = social.icon;
    return (
      <a
        href={social.link}
        key={social.name}
        rel="noopener noreferrer"
        target="_blank"
      >
        <SocialImage>
          <Icon color={theme.colors.fontGrey} />
        </SocialImage>
      </a>
    );
  });

  const linksLeft = linksListLeft.map((link) => (
    <StyledList key={link.label}>
      <StyledAnchor
        color="BLACK"
        href={link.href}
        rel="noopener noreferrer"
        target="_blank"
        isEducator={isEducator}
      >
        {link.label}
      </StyledAnchor>
    </StyledList>
  ));

  const linksRight = linksListRight.map((link) => (
    <StyledList key={link.label}>
      <StyledAnchor
        color="BLACK"
        href={link.href}
        rel="noopener noreferrer"
        target="_blank"
        isEducator={isEducator}
      >
        {link.label}
      </StyledAnchor>
    </StyledList>
  ));

  return (
    <FooterContainer floating={floating} className={className}>
      <FooterWrapper floating={floating}>
        <UNInfo floating={floating}>
          <div>{!hideLogo && <Logo type="footer" />}</div>
          <SocialImagesWrapper floating={floating || hideLogo}>
            {socialIcons}
          </SocialImagesWrapper>
          <P2 color={theme.colors.fontGrey}>
            © 2020 Sorting Hat Technologies Pvt Ltd
          </P2>
        </UNInfo>
        <LeftStyledUL floating={floating}>
          <StyledH7 color={theme.colors.fontLightGrey}>COMPANY</StyledH7>
          {linksLeft}
        </LeftStyledUL>
        <RightStyledUL floating={floating}>
          <StyledH7 color={theme.colors.fontLightGrey}>POPULAR GOALS</StyledH7>
          {linksRight}
        </RightStyledUL>
        {!floating ? (
          <DownloadAppsLearnerWrapper>
            <StyledH7 color={theme.colors.fontLightGrey}>LEARNER APP</StyledH7>
            {learnerApp}
          </DownloadAppsLearnerWrapper>
        ) : null}
        {!floating ? (
          <DownloadAppsEducatorWrapper>
            <StyledH7 color={theme.colors.fontLightGrey}>EDUCATOR APP</StyledH7>
            {educatorApp}
          </DownloadAppsEducatorWrapper>
        ) : null}
        {!floating && (
          <OtherBlock>
            <StyledH7 color={theme.colors.fontLightGrey}>OTHER LINKS</StyledH7>
            <ExtraBlock>
              {extraLinks.map((link) => (
                <Fragment key={link.label}>
                  <StyledAnchor
                    color="BLACK"
                    href={link.href}
                    rel="noopener noreferrer"
                    key={link.label}
                  >
                    {link.label}
                  </StyledAnchor>
                  <Pipe>|</Pipe>
                </Fragment>
              ))}
            </ExtraBlock>
          </OtherBlock>
        )}
      </FooterWrapper>
    </FooterContainer>
  );
};

export default BaseFooter;

BaseFooter.propTypes = {
  display: PropTypes.bool.isRequired,
  noFooter: PropTypes.bool,
  floating: PropTypes.bool,
  hideLogo: PropTypes.bool,
  isEducator: PropTypes.bool,
  className: PropTypes.string
};

BaseFooter.defaultProps = {
  floating: false,
  hideLogo: false,
  isEducator: false,
  noFooter: false,
  className: ''
};
